import React, { useState, useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  IconButton,
  Paper,
  Fade,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ClearIcon from '@mui/icons-material/Clear';
import FileUploadIcon from '@mui/icons-material/FileUpload';

const PhotoUpload = forwardRef(({ onAnalysisStart, onAnalysisComplete, onError, onResetResult, photoUrl }, ref) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Обрабатываем фото из URL, если оно есть
  useEffect(() => {
    if (photoUrl) {
      console.log('📸 PhotoUpload: Отображение фото из URL:', photoUrl);
      setPreview(photoUrl);
    }
  }, [photoUrl]);

  // Предоставляем метод для сброса фото извне
  useImperativeHandle(ref, () => ({
    resetPhoto: () => handleReset()
  }));

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    
    if (!file) return;
    
    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      onError('Пожалуйста, выберите изображение.');
      return;
    }
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      onError('Размер файла слишком большой. Максимальный размер: 10MB.');
      return;
    }
    
    setSelectedFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
    
    // Сбрасываем результаты предыдущего анализа при загрузке новой фотографии
    if (onResetResult) {
      onResetResult();
    }
  };

  const handleUpload = async () => {
    if (!selectedFile && !photoUrl) {
      onError('Пожалуйста, выберите файл.');
      return;
    }

    setIsUploading(true);
    onAnalysisStart();

    const formData = new FormData();
    
    if (selectedFile) {
      formData.append('photo', selectedFile);
    } else if (photoUrl) {
      // Если используется URL, отправляем его как параметр
      formData.append('photo_url', photoUrl);
    }

    try {
      console.log(`🚀 Отправка запроса на сервер: ${selectedFile ? selectedFile.name : 'из URL'}`);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 540000);
      
      const response = await fetch('https://ml.demo.orphanscan.ru/', {
        method: 'POST',
        body: formData,
        headers: {
          'header': 'some text'
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.status === 401) {
        const errorMsg = 'Ошибка аутентификации: Неверный API токен';
        console.error('❌ Ошибка запроса:', errorMsg);
        onError(errorMsg);
        setIsUploading(false);
        return;
      }
      
      const data = await response.json();
      
      // Логируем результат запроса в консоль
      console.log('📊 Результат запроса:', data);
      
      if (data.result === false) {
        const errorMsg = `Произошла ошибка при анализе изображения${data.error ? ': ' + data.error : ''}. Пожалуйста, перезагрузите другую фотографию.`;
        console.error('❌ Ошибка анализа:', errorMsg);
        onError(errorMsg);
        setIsUploading(false);
        return;
      }
      
      // Успешно завершено, передаем данные и сбрасываем состояние загрузки
      console.log('✅ Анализ успешно завершен');
      
      // Логируем структуру данных
      console.log('📋 Данные синдрома Хантера:', data.data.hanter_check_ru || data.data.hanter_check || 'отсутствуют');
      console.log('📋 Данные кожных заболеваний:', data.data.skin_disease_ru || data.data.skin_disease || 'отсутствуют');
      
      onAnalysisComplete(data.data);
      setIsUploading(false);
      
    } catch (error) {
      if (error.name === 'AbortError') {
        const errorMsg = 'Превышено время ожидания (540 секунд). Пожалуйста, повторите попытку позже или попробуйте изображение меньшего размера.';
        console.error('❌ Ошибка таймаута:', errorMsg);
        onError(errorMsg);
      } else {
        const errorMsg = `Ошибка сервера: ${error.message}. Пожалуйста, перезагрузите другую фотографию.`;
        console.error('❌ Ошибка сервера:', error);
        onError(errorMsg);
      }
      setIsUploading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setPreview('');
    setIsUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    // Сбрасываем результаты при очистке фото
    if (onResetResult) {
      onResetResult();
      // Redirect to the homepage when result is reset
      window.location.href = '/';
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Определяем, можем ли мы анализировать
  const canAnalyze = !!selectedFile || !!photoUrl;

  return (
    <Card elevation={1} sx={{ mb: 4, overflow: 'visible', borderRadius: 2 }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h2" component="h2" gutterBottom sx={{ fontSize: '1.5rem', color: 'primary.main', mb: 1 }}>
          Загрузить фото
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Загрузите фото для диагностического анализа
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
          <Box>
            <input
              type="file"
              id="photo-upload"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              ref={fileInputRef}
            />
            
            <Paper 
              elevation={0}
              sx={{
                width: '100%',
                height: 180,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px dashed',
                borderColor: 'rgba(0, 0, 0, 0.12)',
                borderRadius: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                cursor: 'pointer',
                mb: 2,
              }}
              onClick={triggerFileInput}
            >
              <CloudUploadIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1, opacity: 0.8 }} />
              <Typography variant="body2" align="center" sx={{ color: 'text.secondary' }}>
                Перетащите файл сюда или нажмите для выбора
              </Typography>
              <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
                PNG, JPG или JPEG (макс. 10MB)
              </Typography>
            </Paper>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="contained"
                disabled={!canAnalyze || isUploading}
                startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <FileUploadIcon />}
                onClick={handleUpload}
                sx={{ 
                  px: 4, 
                  py: 1, 
                  backgroundColor: 'primary.main', 
                  '&:hover': { backgroundColor: 'primary.dark' },
                  textTransform: 'none',
                }}
              >
                {isUploading ? 'Анализ...' : 'Анализировать фото'}
              </Button>
            </Box>
          </Box>
          
          <Fade in={!!preview}>
            <Box sx={{ mt: 3, position: 'relative' }}>
              {preview && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Предпросмотр:
                    </Typography>
                    <IconButton 
                      color="default"
                      onClick={handleReset}
                      size="small"
                      aria-label="Очистить"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      height: 200,
                      borderRadius: 1,
                      overflow: 'hidden',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      position: 'relative',
                      '& img': {
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                      },
                    }}
                  >
                    <img src={preview} alt="Предпросмотр" />
                  </Box>
                </>
              )}
            </Box>
          </Fade>
        </Box>
      </CardContent>
    </Card>
  );
});

export default PhotoUpload; 